import type { JournalRepository } from '../../../application/ports/JournalRepository';
import type { JournalEntry, JournalPeriod } from '../../../domain/journal/JournalEntry';
import type { JournalEntryId, ProfileId } from '../../../domain/shared/ids';
import type { AscendInvictusDatabase } from '../AscendInvictusDatabase';
import { JournalEntryMapper } from '../mappers';
import { executePersistence } from '../translatePersistenceError';

export class DexieJournalRepository implements JournalRepository {
  public constructor(private readonly database: AscendInvictusDatabase) {}

  public save(entry: JournalEntry): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.journalEntries.put(JournalEntryMapper.toRecord(entry));
    });
  }

  public findById(id: JournalEntryId): Promise<JournalEntry | null> {
    return executePersistence('read', async () => {
      const record = await this.database.journalEntries.get(id);
      return record ? JournalEntryMapper.fromRecord(record) : null;
    });
  }

  public findByLogicalDate(
    profileId: ProfileId,
    logicalDate: string,
    period: JournalPeriod,
  ): Promise<JournalEntry | null> {
    return executePersistence('read', async () => {
      const record = await this.database.journalEntries
        .where('[profileId+logicalDate+period]')
        .equals([profileId, logicalDate, period])
        .first();
      return record ? JournalEntryMapper.fromRecord(record) : null;
    });
  }

  public listByProfile(profileId: ProfileId): Promise<readonly JournalEntry[]> {
    return executePersistence('read', async () =>
      (await this.database.journalEntries.where('profileId').equals(profileId).toArray()).map(
        (record) => JournalEntryMapper.fromRecord(record),
      ),
    );
  }

  public delete(id: JournalEntryId): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.journalEntries.delete(id);
    });
  }

  public clearForProfile(profileId: ProfileId): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.journalEntries.where('profileId').equals(profileId).delete();
    });
  }
}
